import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import App from './App';
import reportWebVitals from './reportWebVitals';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

/**
 * Intervalo de verificacao da versao publicada no CloudFront.
 * Em milissegundos, determina a frequencia do polling de atualizacao.
 */
const APP_UPDATE_CHECK_INTERVAL_MS = 60000;
const BUILD_SIGNATURE_STORAGE_KEY = 'app-build-signature';

const AutoUpdateNoCache = () => {
  const currentBuildSignature = useRef(
    window.localStorage.getItem(BUILD_SIGNATURE_STORAGE_KEY),
  );

  useEffect(() => {
    /**
     * Forca navegacao para uma URL unica e limpa caches locais para evitar
     * reutilizacao de assets antigos apos detectar nova build.
     */
    const forceLoadLatestBuild = async () => {
      try {
        if ('serviceWorker' in navigator) {
          const registrations =
            await navigator.serviceWorker.getRegistrations();
          await Promise.all(
            registrations.map((registration) => registration.unregister()),
          );
        }

        if ('caches' in window) {
          const cacheKeys = await window.caches.keys();
          await Promise.all(cacheKeys.map((key) => window.caches.delete(key)));
        }
      } catch (error) {
        console.warn(`[AutoUpdate] Falha ao limpar cache local:`, error);
      }

      const url = new URL(window.location.href);
      window.location.replace(url.toString());
    };

    /**
     * Recupera uma assinatura da build atual publicada.
     * Prioriza o asset-manifest.json e usa index.html como fallback
     * para evitar falha durante janelas curtas de deploy.
     *
     * @returns Assinatura da build atual ou null quando indisponivel.
     */
    const getBuildSignature = async (): Promise<string | null> => {
      try {
        // Tenta buscar o asset-manifest.json com bypass de cache
        const response = await fetch(`/asset-manifest.json?t=${Date.now()}`, {
          cache: 'no-store',
          headers: {
            'cache-control': 'no-cache',
          },
        });

        if (!response.ok) {
          console.warn(
            `[AutoUpdate] asset-manifest.json indisponivel (${response.status}), tentando fallback`,
          );

          // Fallback: tenta buscar index.html como indicador de novo deploy
          const indexResponse = await fetch(`/?t=${Date.now()}`, {
            cache: 'no-store',
            headers: {
              'cache-control': 'no-cache',
            },
          });

          if (!indexResponse.ok) {
            return null;
          }

          // Usa metadados do HTML para manter uma assinatura deterministica.
          const etag = indexResponse.headers.get('etag') || '';
          const lastModified = indexResponse.headers.get('last-modified') || '';
          const contentLength =
            indexResponse.headers.get('content-length') || '';

          return `index|${etag}|${lastModified}|${contentLength}`;
        }

        const manifest = await response.json();
        const mainJs = manifest?.files?.['main.js'];
        const entrypoints = Array.isArray(manifest?.entrypoints)
          ? manifest.entrypoints.join('|')
          : '';

        return mainJs || entrypoints || null;
      } catch (error) {
        console.warn(`[AutoUpdate] Erro ao verificar versao:`, error);
        return null;
      }
    };

    /**
     * Compara a assinatura atual do cliente com a assinatura publicada.
     * Quando detecta mudanca de versao, recarrega a aplicacao para
     * garantir que o totem use o build mais recente.
     */
    const checkForNewVersion = async () => {
      try {
        const latestBuildSignature = await getBuildSignature();

        if (!latestBuildSignature) {
          return;
        }

        if (!currentBuildSignature.current) {
          currentBuildSignature.current = latestBuildSignature;
          window.localStorage.setItem(
            BUILD_SIGNATURE_STORAGE_KEY,
            latestBuildSignature,
          );
          console.log(
            `[AutoUpdate] Versao inicial registrada:`,
            latestBuildSignature,
          );
          return;
        }

        if (currentBuildSignature.current !== latestBuildSignature) {
          currentBuildSignature.current = latestBuildSignature;
          window.localStorage.setItem(
            BUILD_SIGNATURE_STORAGE_KEY,
            latestBuildSignature,
          );
          console.log(
            `[AutoUpdate] Nova versao detectada! Recarregando aplicacao...`,
          );
          await forceLoadLatestBuild();
        }
      } catch (error) {
        console.error(`[AutoUpdate] Erro ao verificar atualizacao:`, error);
      }
    };

    // Executa verificacao inicial
    checkForNewVersion();

    // Agenda verificacoes periodicas
    const intervalId = window.setInterval(
      checkForNewVersion,
      APP_UPDATE_CHECK_INTERVAL_MS,
    );

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  return null;
};

ReactDOM.render(
  <React.StrictMode>
    <AutoUpdateNoCache />
    <App />
  </React.StrictMode>,
  document.getElementById('root'),
);

serviceWorkerRegistration.register();
reportWebVitals();
